import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Video, ResizeMode } from 'expo-av';
import { apiService } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface NodeMediaPickerFieldProps {
  label?: string;
  mediaUrl?: string;
  mediaType?: string;
  onChange: (patch: { mediaUrl?: string; mediaType?: string }) => void;
}

/** Media attach/upload control for flow nodes (send_message, write_post) —
 * same pick-then-upload flow as the post composer (PostCreateScreen), so a
 * business builds a flow node's media the same way they'd attach media to a
 * real post, not by pasting a URL. */
export const NodeMediaPickerField: React.FC<NodeMediaPickerFieldProps> = ({ label = 'Media (optional)', mediaUrl, mediaType, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const [uploading, setUploading] = useState(false);

  const pickMedia = async () => {
    try {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert('Permission needed', 'Please allow access to continue.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.All, quality: 0.8 });
      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0];
      const isVideo = asset.type === 'video';
      setUploading(true);
      try {
        const fileName = `flow_${Date.now()}.${isVideo ? 'mp4' : 'jpg'}`;
        const mimeType = isVideo ? 'video/mp4' : 'image/jpeg';
        const uploadResult = await apiService.uploadMedia(asset.uri, fileName, mimeType, 'default-facebook-messenger');
        if (uploadResult.statusCode === 1000 && uploadResult.data?.mediaUrl) {
          onChange({ mediaUrl: uploadResult.data.mediaUrl, mediaType: isVideo ? 'video' : 'image' });
        } else {
          Alert.alert('Upload failed', 'Could not upload media, try again');
        }
      } finally {
        setUploading(false);
      }
    } catch (error: any) {
      Alert.alert('Upload failed', error?.message || '');
    }
  };

  return (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      {mediaUrl ? (
        <View style={styles.previewWrap}>
          {mediaType === 'video' ? (
            <Video source={{ uri: mediaUrl }} style={styles.preview} resizeMode={ResizeMode.COVER} useNativeControls={false} />
          ) : (
            <Image source={{ uri: mediaUrl }} style={styles.preview} resizeMode="cover" />
          )}
          <TouchableOpacity style={styles.removeButton} onPress={() => onChange({ mediaUrl: undefined, mediaType: undefined })}>
            <Ionicons name="close-circle" size={22} color={colors.error as string} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity style={styles.pickButton} onPress={pickMedia} disabled={uploading} activeOpacity={0.7}>
          {uploading ? (
            <ActivityIndicator size="small" color={colors.primary as string} />
          ) : (
            <>
              <Ionicons name="image-outline" size={18} color={colors.primary as string} />
              <Text style={styles.pickButtonText}>Attach photo or video</Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  field: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  pickButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    height: 44,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  pickButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  previewWrap: {
    width: 120,
    height: 120,
    borderRadius: designTokens.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: colors.background.base,
  },
  preview: {
    width: '100%',
    height: '100%',
  },
  removeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: colors.background.surface,
    borderRadius: 11,
  },
});
